'use client'

import { useEffect } from 'react'
import { toast } from 'sonner'

/**
 * Registers the service worker for PWA offline support.
 * This component handles service worker registration on the client side.
 * In development, it unregisters any existing service workers to prevent HMR issues.
 * In production, shows a toast notification when updates are available.
 */
export function ServiceWorkerRegister() {
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return
    }

    // In development, unregister any existing service workers to prevent HMR issues
    if (process.env.NODE_ENV === 'development') {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          registration.unregister().then((success) => {
            if (success) {
              console.log('Service Worker unregistered in development')
            }
          })
        }
      })
      return
    }

    // Production: Register service worker
    let updateTimer: ReturnType<typeof setInterval> | undefined

    navigator.serviceWorker
      .register('/sw.js', {
        scope: '/'
      })
      .then((registration) => {
        console.log('Service Worker registered:', registration.scope)

        // Check for updates periodically (every hour)
        updateTimer = setInterval(
          () => {
            registration.update()
          },
          60 * 60 * 1000
        )

        // Handle new service worker installation
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (
                newWorker.state === 'installed' &&
                navigator.serviceWorker.controller
              ) {
                // New service worker available, prompt user to update
                toast.info('App update available', {
                  description: 'A new version is ready. Refresh to update.',
                  duration: Infinity,
                  action: {
                    label: 'Refresh',
                    onClick: () => {
                      const waitingWorker = registration.waiting

                      if (!waitingWorker) {
                        window.location.reload()
                        return
                      }

                      navigator.serviceWorker.addEventListener(
                        'controllerchange',
                        () => window.location.reload(),
                        { once: true }
                      )
                      waitingWorker.postMessage({ type: 'SKIP_WAITING' })
                    }
                  },
                  dismissible: true
                })
              }
            })
          }
        })
      })
      .catch((error) => {
        console.error('Service Worker registration failed:', error)
      })

    return () => {
      if (updateTimer) clearInterval(updateTimer)
    }
  }, [])

  return null
}
